import { calendarModeMap } from '../../../../shared/configs/searchParams';
import { dateFormats, formatDate } from '../../../../shared/packages/dateTime';

class ModeSegment {
  constructor(period, calcPageCount) {
    this._period = period;
    this._pageCount = calcPageCount(period);
    this._periodAsParams = new Map();
    this._dates = new Map();
  }

  get pageCount() {
    return this._pageCount;
  }

  getPeriodAsParams(page) {}
  getDates(page) {}

  _formatToIso(date) {
    return formatDate(date, dateFormats.ISO);
  }

  _clampFrom(date) {
    const fromDate = this._period.fromDate;
    return this._formatToIso(date.isBefore(fromDate) ? fromDate : date);
  }

  _clampTo(date) {
    const toDate = this._period.toDate;
    return this._formatToIso(date.isAfter(toDate) ? toDate : date);
  }

  _createDates(fromDate, toDate) {
    const result = [];
    const current = fromDate.clone();
    const end = toDate.clone();

    while (current.isSameOrBefore(end, 'day')) {
      const date = current.clone();
      result.push([date, this._formatToIso(date)]);
      current.add(1, 'day');
    }

    return result;
  }
}

class DayModeSegment extends ModeSegment {
  constructor(period) {
    super(period, ({ fromDate, toDate }) => toDate.diff(fromDate, 'days') + 1);
  }

  getPeriodAsParams(page) {
    let result = this._periodAsParams.get(page);

    if (!result) {
      const offset = page - 1;
      const date = this._period.fromDate.clone().add(offset, 'days');

      result = { fromDate: this._clampFrom(date), toDate: this._clampTo(date) };

      this._periodAsParams.set(page, result);
    }

    return result;
  }

  getDates(page) {
    let result = this._dates.get(page);

    if (!result) {
      const offset = page - 1;
      const date = this._period.fromDate.clone().add(offset, 'days');

      result = this._createDates(date, date);

      this._dates.set(page, result);
    }

    return result;
  }

  getPageForDate(date) {
    const { fromDate, toDate } = this._period;
    if (date.isBefore(fromDate, 'day')) return 1;
    if (date.isAfter(toDate, 'day')) return this._pageCount;

    return (
      date.startOf('day').diff(fromDate.clone().startOf('day'), 'days') + 1
    );
  }
}

class WeekModeSegment extends ModeSegment {
  constructor(period) {
    super(period, ({ fromDate, toDate }) => {
      const start = fromDate.clone().startOf('isoWeek');
      const end = toDate.clone().startOf('isoWeek');
      return end.diff(start, 'weeks') + 1;
    });
  }

  getPeriodAsParams(page) {
    let result = this._periodAsParams.get(page);

    if (!result) {
      const offset = page - 1;

      const start = this._period.fromDate.clone().startOf('isoWeek');
      const fromDate = start.clone().add(offset, 'weeks');
      const toDate = fromDate.clone().add(6, 'days');

      result = {
        fromDate: this._clampFrom(fromDate),
        toDate: this._clampTo(toDate),
      };

      this._periodAsParams.set(page, result);
    }

    return result;
  }

  getDates(page) {
    let result = this._dates.get(page);

    if (!result) {
      const offset = page - 1;

      const start = this._period.fromDate.clone().startOf('isoWeek');

      const fromDate = start.clone().add(offset, 'weeks');
      const toDate = fromDate.clone().add(6, 'days');

      result = this._createDates(fromDate, toDate);

      this._dates.set(page, result);
    }

    return result;
  }

  getPageForDate(date) {
    const { fromDate, toDate } = this._period;
    if (date.isBefore(fromDate, 'day')) return 1;
    if (date.isAfter(toDate, 'day')) return this._pageCount;

    const start = fromDate.clone().startOf('isoWeek');
    const target = date.clone().startOf('isoWeek');

    return target.diff(start, 'weeks') + 1;
  }
}

class MonthModeSegment extends ModeSegment {
  constructor(period) {
    super(period, ({ fromDate, toDate }) => {
      const start = fromDate.clone().startOf('month');
      const end = toDate.clone().startOf('month');
      return end.diff(start, 'months') + 1;
    });
  }

  getPeriodAsParams(page) {
    let result = this._periodAsParams.get(page);

    if (!result) {
      const offset = page - 1;
      const start = this._period.fromDate.clone().startOf('month');
      const fromDate = start.clone().add(offset, 'months');
      const toDate = fromDate.clone().add(1, 'month').subtract(1, 'day');

      result = {
        fromDate: this._clampFrom(fromDate),
        toDate: this._clampTo(toDate),
      };

      this._periodAsParams.set(page, result);
    }

    return result;
  }

  getDates(page) {
    let result = this._dates.get(page);

    if (!result) {
      const offset = page - 1;
      const start = this._period.fromDate.clone().startOf('month');
      const fromDate = start.clone().add(offset, 'months');
      const toDate = fromDate.clone().add(1, 'month').subtract(1, 'day');

      result = this._createDates(fromDate, toDate);

      this._dates.set(page, result);
    }

    return result;
  }

  getPageForDate(date) {
    const { fromDate, toDate } = this._period;
    if (date.isBefore(fromDate, 'day')) return 1;
    if (date.isAfter(toDate, 'day')) return this._pageCount;

    const start = fromDate.clone().startOf('month');
    const target = date.clone().startOf('month');

    return target.diff(start, 'months') + 1;
  }
}

const segments = {
  [calendarModeMap.day]: DayModeSegment,
  [calendarModeMap.week]: WeekModeSegment,
  [calendarModeMap.month]: MonthModeSegment,
};

export class ModeSegmentFactory {
  constructor(period) {
    this._period = period;
    this._segments = {};
  }

  getSegment(key) {
    if (!this._segments[key] && segments[key]) {
      const Segment = segments[key];
      this._segments[key] = new Segment(this._period);
    }

    return this._segments[key];
  }
}
